<script setup>
// The advanced field of a question: the "answer hint" (how the Max recognizes the answer, often a pattern), with a
// quick test in the browser. Type what a person might say and press "Test" to see whether the hint catches it.
// Nothing is sent. The test runs on demand (not on each key), on short texts, and a pattern that repeats a
// repetition is refused with a warning, so that a bad pattern cannot freeze the page.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { HINT_LIMIT, testHint } from './scenarioStep';

defineProps({
  id: { type: String, required: true },
});
const hint = defineModel({ type: String, default: '' });
const { t } = useI18n();
const sample = ref('');
const result = ref({ state: 'empty' });
const run = () => {
  result.value = testHint(hint.value, sample.value);
};
// what was tested is no longer what is typed: the answer goes away
watch([hint, sample], () => {
  result.value = { state: 'empty' };
});
const KEYS = {
  invalid: 'INVALID',
  unsafe: 'UNSAFE',
  too_long: 'TOO_LONG',
  match: 'MATCH',
  miss: 'MISS',
};
const messageKey = computed(() => KEYS[result.value.state]);
</script>

<template>
  <div class="grid gap-2" data-answer-hint>
    <Label :for="`${id}-hint`">
      {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.LABEL') }}
    </Label>
    <Input
      :id="`${id}-hint`"
      v-model="hint"
      class="font-mono"
      :aria-describedby="`${id}-hint-help`"
    />
    <p
      :id="`${id}-hint-help`"
      class="type-polaris-caption text-polaris-muted-foreground"
    >
      {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.HELP') }}
    </p>
    <Label :for="`${id}-sample`">
      {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.TEST_LABEL') }}
    </Label>
    <div class="flex items-center gap-2">
      <Input
        :id="`${id}-sample`"
        v-model="sample"
        :maxlength="HINT_LIMIT"
        class="flex-1"
        :placeholder="
          t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.TEST_PLACEHOLDER')
        "
        @keydown.enter.prevent="run"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-hint-run
        @click="run"
      >
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.RUN') }}
      </Button>
    </div>
    <p
      v-if="messageKey"
      class="type-polaris-caption"
      :class="
        result.state === 'match'
          ? 'text-polaris-foreground'
          : ['unsafe', 'invalid'].includes(result.state)
            ? 'text-polaris-destructive'
            : 'text-polaris-muted-foreground'
      "
      role="status"
      :data-hint-result="result.state"
    >
      {{
        t(`POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HINT.${messageKey}`, {
          limit: HINT_LIMIT,
          matched: result.matched,
        })
      }}
    </p>
  </div>
</template>
